import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import AuthContext from './authContext';
import CountContext from './countContext';
import useCall from '../hooks/useCall';

const noopPromise = () => Promise.resolve();

const CallContext = React.createContext({
  selectedAgent: null,
  setSelectedAgent: () => {},
  availableAgents: [],
  setAvailableAgents: () => {},
  calls: [],
  setCalls: () => {},
  isLoading: false,
  error: null,
  initiateCallback: noopPromise,
  initiateDial: noopPromise,
  deleteCall: noopPromise,
  getAvailableAgents: noopPromise,
  getOpenCallbacks: noopPromise,
  getCallCount: noopPromise,
  shouldFetchCalls: false,
  setShouldFetchCalls: () => {},
});

const SELECTED_AGENT_KEY = 'selectedComdeskAgent';

export const CallContextProvider = ({ children }) => {
  const [selectedAgent, setSelectedAgentState] = useState(null);
  const [availableAgents, setAvailableAgentsState] = useState([]);
  const [calls, setCalls] = useState([]);
  const [shouldFetchCalls, setShouldFetchCallsState] = useState(false);

  const authCtx = useContext(AuthContext);
  const countCtx = useContext(CountContext);
  const {
    isLoading,
    error,
    initiateCallback,
    initiateDial,
    deleteCall,
    getAvailableAgents,
    getOpenCallbacks,
    getCallCount,
  } = useCall();

  // Load selected agent from localStorage and initialize
  useEffect(() => {
    const initializeCallContext = async () => {
      try {
        // Load available agents first
        const response = await getAvailableAgents();
        setAvailableAgentsState(response.agents || []);

        // If there are no agents, ensure everything is cleared and counters are stopped
        if (!response.agents || response.agents.length === 0) {
          setSelectedAgentState(null);
          localStorage.removeItem(SELECTED_AGENT_KEY);
          setShouldFetchCallsState(false);
          countCtx.unregisterInterval('calls');
          countCtx.setCount('calls', 0);
          return;
        }

        // Load selected agent from localStorage
        const savedAgent = localStorage.getItem(SELECTED_AGENT_KEY);
        if (savedAgent) {
          const agent = JSON.parse(savedAgent);

          if (response.agents.find(a => a.id === agent.id)) {
            setSelectedAgentState(agent);
          } else {
            setSelectedAgentState(null);
            localStorage.removeItem(SELECTED_AGENT_KEY);
          }
        } else if (response.agents && response.agents.length === 1) {
          // Auto-select the only available agent
          const soleAgent = response.agents[0];
          setSelectedAgentState(soleAgent);
          localStorage.setItem(SELECTED_AGENT_KEY, JSON.stringify(soleAgent));
        }
      } catch (error) {
        console.error('Error initializing call context:', error);
      }
    };

    if (authCtx.token) {
      initializeCallContext();
    }
  }, [authCtx.token]);

  // Determine if the current user has the right to read own calls
  const hasCallReadRight = useMemo(() => {
    if (!authCtx || typeof authCtx.checkRightAny !== 'function') {
      return false;
    }
    return authCtx.checkRightAny('call:read:own');
  }, [authCtx.checkRightAny]);

  // Handle agent selection with localStorage persistence
  const setSelectedAgent = useCallback(agent => {
    if (agent) {
      setSelectedAgentState(agent);
      localStorage.setItem(SELECTED_AGENT_KEY, JSON.stringify(agent));
    } else {
      setSelectedAgentState(null);
      localStorage.removeItem(SELECTED_AGENT_KEY);
    }
  }, []);

  // Handle shouldFetchCalls state
  const setShouldFetchCalls = useCallback(value => {
    setShouldFetchCallsState(value);
  }, []);

  // Set up polling interval for calls
  useEffect(() => {
    const fetchCalls = async () => {
      if (!selectedAgent || !shouldFetchCalls) {
        setCalls([]);
        return;
      }

      try {
        const callsData = await getOpenCallbacks(selectedAgent.id);
        setCalls(callsData);
      } catch (error) {
        console.error('Error fetching calls:', error);
        setCalls([]);
      }
    };

    const setUpdateInterval = async () => {
      // Ensure the user has the required right before starting the counter
      if (!hasCallReadRight) {
        countCtx.unregisterInterval('calls');
        countCtx.setCount('calls', 0);
        return () => {
          countCtx.unregisterInterval('calls');
        };
      }

      if (selectedAgent && selectedAgent.skillGroups && selectedAgent.skillGroups.length > 0) {
        const fetchCount = async () => {
          try {
            const count = await getCallCount(selectedAgent.skillGroups);
            countCtx.setCount('calls', count);
          } catch (error) {
            console.error('Error fetching call count:', error);
            countCtx.setCount('calls', 0);
          }
        };

        // Execute immediately
        fetchCount();

        // Register interval
        countCtx.registerInterval('calls', fetchCount);
      } else {
        countCtx.unregisterInterval('calls');
        countCtx.setCount('calls', 0);
      }

      return () => {
        countCtx.unregisterInterval('calls');
      };
    };

    // Always set up the interval management, even if no agent is selected
    setUpdateInterval();

    // Only set up call fetching if an agent is selected and shouldFetchCalls is true
    if (selectedAgent && shouldFetchCalls) {
      // Execute immediately
      fetchCalls();

      const interval = setInterval(fetchCalls, 10 * 1000);

      return () => clearInterval(interval);
    }
  }, [selectedAgent, shouldFetchCalls, authCtx.token, hasCallReadRight]);

  // Reset context when token changes
  useEffect(() => {
    if (!authCtx.token) {
      setSelectedAgentState(null);
      setAvailableAgentsState([]);
      setCalls([]);
      setShouldFetchCallsState(false);
      countCtx.unregisterInterval('calls');
      countCtx.setCount('calls', 0);
    }
  }, [authCtx.token]);

  const contextValue = {
    selectedAgent,
    setSelectedAgent,
    availableAgents,
    setAvailableAgents: setAvailableAgentsState,
    calls,
    setCalls,
    isLoading,
    error,
    initiateCallback,
    initiateDial,
    deleteCall,
    getAvailableAgents,
    getOpenCallbacks,
    getCallCount,
    shouldFetchCalls,
    setShouldFetchCalls,
  };

  return <CallContext.Provider value={contextValue}>{children}</CallContext.Provider>;
};

export default CallContext;
